import { useEffect, useMemo, useState } from 'react'
import { Modal } from '@/components/common'
import { teacherApi, type RequestLesson } from '@/services/api/teacher'
import { teacherPriorityApi } from '@/services/api/teacherPriority'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'
import { extractErrorMessage } from '@/utils/errorHandler'
import { dispatchCascadeStateChanged } from '@/utils/cascadeEvents'
import { getLessonRowStyle } from '@/utils/lessonRowColors'
import { sortLessonsByDateTime } from '@/utils/lessonSort'
import { useAuthStore } from '@/store'

interface StornoModalProps {
  isOpen: boolean
  onClose: () => void
  requestId: string
  /** Who is initiating the storno. */
  initiatorRole: 'requester' | 'substitute'
  /** Lesson IDs to pre-select when the modal opens (e.g. clicked per-lesson cancel X). */
  preselectedLessonIds?: string[]
  onSuccess?: () => void
}

function isCommitted(lesson: RequestLesson): boolean {
  return Boolean(lesson.assigned_teacher_id)
    && ['accepted', 'assigned', 'confirmed', 'filled', 'booked'].includes(lesson.status)
}

function formatDate(iso: string | null): string {
  if (!iso) return 'Ohne Datum'
  try {
    return new Date(iso).toLocaleDateString('de-CH', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })
  } catch {
    return iso
  }
}

function formatTableDate(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('de-CH', { weekday: 'short', day: '2-digit', month: '2-digit', year: '2-digit' })
  } catch {
    return iso
  }
}

function renderLessonStatus(lesson: RequestLesson, textClass: string) {
  const state = lesson.assignment_state || lesson.status
  const code = lesson.assigned_teacher_short_code
  const name = lesson.assigned_teacher_name

  if (state === 'cancelled') {
    return <span className={`text-xs ${textClass}`}>✗ Storniert</span>
  }
  if (state === 'confirmed' || state === 'accepted') {
    return (
      <span className={`text-xs font-medium ${textClass}`}>
        ✓ {code || name || 'Übernommen'}
        {name && code && <span className="block text-[10px] font-normal">{name}</span>}
      </span>
    )
  }
  if (state === 'declined') {
    return <span className={`text-xs ${textClass}`}>✗ Abgelehnt</span>
  }
  if (state === 'waiting') {
    return <span className={`text-xs ${textClass}`}>◷ Wartepool</span>
  }
  return <span className={`text-xs ${textClass}`}>○ Offen</span>
}

export function StornoModal({ isOpen, onClose, requestId, initiatorRole, preselectedLessonIds, onSuccess }: StornoModalProps) {
  const viewerTeacherId = useAuthStore(state => state.user?.teacher_id)
  const [lessons, setLessons] = useState<RequestLesson[]>([])
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setLoading(true)
    setError(null)
    setSelected(new Set())
    setReason('')
    teacherApi
      .getRequestLessons(requestId)
      .then(data => {
        const cancellable = (data.lessons || []).filter(l => l.status !== 'cancelled')
        setLessons(cancellable)
        if (preselectedLessonIds?.length) {
          const validIds = new Set(cancellable.map(l => l.id))
          setSelected(new Set(preselectedLessonIds.filter(id => validIds.has(id))))
        }
      })
      .catch(e => setError(extractErrorMessage(e, { loadError: 'Lektionen konnten nicht geladen werden.' })))
      .finally(() => setLoading(false))
  }, [isOpen, requestId, preselectedLessonIds])

  const filteredLessons = useMemo(() => {
    if (initiatorRole === 'requester') return lessons
    return lessons.filter(l => {
      if (l.assigned_teacher_id && l.assigned_teacher_id === viewerTeacherId) return true
      if (l.assignment_state === 'waiting') return true
      return false
    })
  }, [lessons, initiatorRole, viewerTeacherId])

  // Group lessons by coverage block (true blocks), fall back to date
  const groups = useMemo(() => {
    const map = new Map<string, { label: string; date: string | null; lessons: RequestLesson[] }>()
    for (const l of sortLessonsByDateTime(filteredLessons)) {
      const key = l.coverage_block_id || `date:${l.date || 'Ohne Datum'}`
      if (!map.has(key)) {
        map.set(key, { label: l.coverage_block_id ? `Block ${map.size + 1}` : formatDate(l.date), date: l.date, lessons: [] })
      }
      map.get(key)!.lessons.push(l)
    }
    return Array.from(map.entries()).map(([key, val]) => ({ key, ...val }))
  }, [filteredLessons])

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleGroup = (groupLessons: RequestLesson[]) => {
    const ids = groupLessons.map(l => l.id)
    const allSelected = ids.every(id => selected.has(id))
    setSelected(prev => {
      const next = new Set(prev)
      if (allSelected) ids.forEach(id => next.delete(id))
      else ids.forEach(id => next.add(id))
      return next
    })
  }

  const selectAll = () => {
    setSelected(new Set(filteredLessons.map(l => l.id)))
  }

  const deselectAll = () => {
    setSelected(new Set())
  }

  const everythingSelected = filteredLessons.length > 0 && filteredLessons.every(l => selected.has(l.id))
  const nothingSelected = selected.size === 0

  const hasCommitted = filteredLessons.some(l => selected.has(l.id) && isCommitted(l))

  const handleSubmit = async () => {
    if (selected.size === 0) {
      setError('Bitte wählen Sie mindestens eine Lektion aus.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await teacherPriorityApi.createCancellationRequest({
        substitution_request_id: requestId,
        lesson_ids: Array.from(selected),
        reason: reason.trim() || undefined,
      })
      dispatchCascadeStateChanged(requestId)
      onSuccess?.()
      onClose()
    } catch (e) {
      setError(extractErrorMessage(e, { loadError: 'Storno konnte nicht erstellt werden.' }))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Stornierung" size="lg">
      <p className="text-sm text-gray-600 mb-1">
        Wählen Sie die Lektionen, die storniert werden sollen. Sie können ganze Tage (Blöcke) oder
        einzelne Lektionen auswählen.
      </p>
      <p className="text-xs text-gray-500 mb-4">
        {initiatorRole === 'requester'
          ? 'Offene Lektionen werden sofort storniert (keine Ersatzsuche). Bereits übernommene Lektionen müssen von der jeweiligen Lehrperson bestätigt werden.'
          : 'Offene Lektionen werden freigegeben und neu vergeben. Bereits von Ihnen übernommene Lektionen müssen vom anfragenden Lehrer bestätigt werden.'}
      </p>

      {!loading && groups.length > 0 && (
        <div className="flex items-center justify-end gap-2 mb-3">
          <button
            onClick={selectAll}
            disabled={submitting || everythingSelected}
            className="px-2 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Alle auswählen
          </button>
          <button
            onClick={deselectAll}
            disabled={submitting || nothingSelected}
            className="px-2 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Alle abwählen
          </button>
        </div>
      )}

      {loading && <div className="text-sm text-gray-500 py-6 text-center">Lektionen werden geladen…</div>}

      {!loading && groups.length === 0 && (
        <div className="text-sm text-gray-500 py-6 text-center">Keine stornierbaren Lektionen vorhanden.</div>
      )}

      {!loading && groups.map(group => {
        const ids = group.lessons.map(l => l.id)
        const allSelected = ids.every(id => selected.has(id))
        return (
          <div key={group.key} className="border rounded-lg p-3 bg-gray-50 mb-3">
            <div className="flex items-center justify-between mb-2">
              <label className="flex items-center gap-2 cursor-pointer font-medium text-sm text-gray-800">
                <input
                  type="checkbox"
                  className="w-4 h-4"
                  checked={allSelected}
                  onChange={() => toggleGroup(group.lessons)}
                />
                {group.label} · {group.lessons.length} Lektionen
              </label>
            </div>
            <div className="overflow-x-auto rounded-md border border-gray-200">
              <table className="w-full text-xs bg-white" aria-label={`Lektionen in ${group.label}`}>
                <thead className="text-[10px] text-gray-500 uppercase bg-gray-100 border-b">
                  <tr>
                    <th scope="col" className="py-1.5 px-2 text-left w-8"></th>
                    <th scope="col" className="py-1.5 px-2 text-left font-medium">Datum</th>
                    <th scope="col" className="py-1.5 px-2 text-left font-medium">Zeit</th>
                    <th scope="col" className="py-1.5 px-2 text-left font-medium">Fach</th>
                    <th scope="col" className="py-1.5 px-2 text-left font-medium">Klasse</th>
                    <th scope="col" className="py-1.5 px-2 text-left font-medium">Raum</th>
                    <th scope="col" className="py-1.5 px-2 text-right font-medium">Übernommen von / Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {group.lessons.map(l => {
                    const committed = isCommitted(l)
                    const style = getLessonRowStyle(
                      l.assignment_state || l.status,
                      l.assigned_teacher_id,
                      viewerTeacherId,
                      initiatorRole === 'requester' ? 'owner' : 'viewer',
                    )
                    return (
                      <tr key={l.id} className={style.row}>
                        <td className="py-1.5 px-2">
                          <input
                            type="checkbox"
                            className="w-4 h-4"
                            checked={selected.has(l.id)}
                            onChange={() => toggle(l.id)}
                            aria-label={`Lektion ${l.time_slot_name || ''} ${l.subject_name || ''} auswählen`}
                          />
                        </td>
                        <td className={`py-1.5 px-2 ${style.text}`}>{formatTableDate(l.date)}</td>
                        <td className={`py-1.5 px-2 ${style.text}`}>{l.time_slot_name || '—'}</td>
                        <td className={`py-1.5 px-2 font-medium ${style.text}`}>{l.subject_name || '—'}</td>
                        <td className={`py-1.5 px-2 ${style.text}`}>{l.klass_name || '—'}</td>
                        <td className={`py-1.5 px-2 ${style.text}`}>{l.room_name || '—'}</td>
                        <td className="py-1.5 px-2 text-right">
                          <div className="flex flex-col items-end gap-0.5">
                            {renderLessonStatus(l, style.text)}
                            {committed && (
                              <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                                <AlertTriangle className="w-2.5 h-2.5" /> muss bestätigt werden
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )
      })}

      {hasCommitted && (
        <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Für bereits übernommene Lektionen wird eine Storno-Anfrage verschickt (Radar, E-Mail,
            „Anfragen an mich"). Erst nach Zustimmung wird die jeweilige Lektion storniert.
          </span>
        </div>
      )}

      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">Grund (optional)</label>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={3}
          className="w-full rounded-md border border-gray-300 shadow-sm px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          placeholder="Optional: Begründung für die Stornierung…"
        />
      </div>

      {error && <div className="p-3 bg-red-100 text-red-700 rounded text-sm mb-3">{error}</div>}

      <div className="flex justify-end gap-3 pt-2 border-t">
        <button
          onClick={onClose}
          disabled={submitting}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
        >
          Abbrechen
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting || selected.size === 0}
          className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 text-sm"
        >
          <CheckCircle2 className="w-4 h-4" />
          {submitting ? 'Wird storniert…' : `${selected.size} stornieren`}
        </button>
      </div>
    </Modal>
  )
}
